import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

const updateFieldValue = (setFormData, clearFieldError, fieldName, nextValue) => {
  setFormData((prev) => ({ ...prev, [fieldName]: nextValue }));
  clearFieldError?.(fieldName);
};

export function CrmFormGrid({ children }) {
  return (
    <Box sx={{ mt: 1, display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
      {children}
    </Box>
  );
}

export function CrmTextInput({
  name,
  label,
  formData,
  setFormData,
  errors,
  clearFieldError,
  placeholder,
}) {
  const errorMessage = errors?.[name] || "";

  return (
    <TextField
      fullWidth
      label={label}
      value={formData?.[name] ?? ""}
      onChange={(event) => {
        updateFieldValue(setFormData, clearFieldError, name, event.target.value);
      }}
      placeholder={placeholder}
      error={Boolean(errorMessage)}
      helperText={errorMessage || " "}
    />
  );
}

export function CrmNumberInput({
  name,
  label,
  formData,
  setFormData,
  errors,
  clearFieldError,
  placeholder,
}) {
  const errorMessage = errors?.[name] || "";

  return (
    <TextField
      fullWidth
      type="number"
      label={label}
      value={formData?.[name] ?? ""}
      onChange={(event) => {
        updateFieldValue(setFormData, clearFieldError, name, event.target.value);
      }}
      placeholder={placeholder}
      error={Boolean(errorMessage)}
      helperText={errorMessage || " "}
    />
  );
}

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const normalized = String(value).trim().replace(" ", "T");
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) {
    return normalized;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(normalized)) {
    return normalized;
  }
  return normalized;
};

export function CrmDateTimeInput({
  name,
  label,
  formData,
  setFormData,
  errors,
  clearFieldError,
}) {
  const errorMessage = errors?.[name] || "";

  return (
    <TextField
      fullWidth
      type="datetime-local"
      label={label}
      value={toDateTimeLocalValue(formData?.[name])}
      onChange={(event) => {
        updateFieldValue(setFormData, clearFieldError, name, event.target.value);
      }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ step: 1 }}
      error={Boolean(errorMessage)}
      helperText={errorMessage || " "}
    />
  );
}

const toDateValue = (value) => {
  if (!value) return "";
  const normalized = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
  if (/^\d{4}-\d{2}-\d{2}T/.test(normalized)) return normalized.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}\s/.test(normalized)) return normalized.slice(0, 10);
  return normalized;
};

export function CrmDateInput({
  name,
  label,
  formData,
  setFormData,
  errors,
  clearFieldError,
}) {
  const errorMessage = errors?.[name] || "";

  return (
    <TextField
      fullWidth
      type="date"
      label={label}
      value={toDateValue(formData?.[name])}
      onChange={(event) => {
        updateFieldValue(setFormData, clearFieldError, name, event.target.value);
      }}
      InputLabelProps={{ shrink: true }}
      error={Boolean(errorMessage)}
      helperText={errorMessage || " "}
    />
  );
}

export function CrmSelectInput({
  name,
  label,
  formData,
  setFormData,
  errors,
  clearFieldError,
  options = [],
  nullable = false,
  disabled = false,
}) {
  const errorMessage = errors?.[name] || "";

  return (
    <FormControl fullWidth error={Boolean(errorMessage)}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={formData?.[name] ?? ""}
        label={label}
        disabled={Boolean(disabled)}
        onChange={(event) => {
          updateFieldValue(setFormData, clearFieldError, name, event.target.value);
        }}
      >
        {nullable ? <MenuItem value="">None</MenuItem> : null}
        {(options || []).map((option) => (
          <MenuItem key={`${name}-${option.value}`} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}
    </FormControl>
  );
}

export function CrmCheckboxInput({
  name,
  label,
  formData,
  setFormData,
  errors,
  clearFieldError,
}) {
  const errorMessage = errors?.[name] || "";

  return (
    <FormControl error={Boolean(errorMessage)}>
      <FormControlLabel
        control={(
          <Checkbox
            checked={Boolean(formData?.[name])}
            onChange={(event) => {
              updateFieldValue(setFormData, clearFieldError, name, event.target.checked);
            }}
          />
        )}
        label={label}
      />
      {errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}
    </FormControl>
  );
}

export function CrmColorInput({
  name,
  label,
  formData,
  setFormData,
  errors,
  clearFieldError,
}) {
  const errorMessage = errors?.[name] || "";
  const currentValue = String(formData?.[name] || "").trim();
  const normalizedValue = /^#[0-9A-Fa-f]{6}$/.test(currentValue) ? currentValue : "#3f51b5";

  return (
    <TextField
      fullWidth
      type="color"
      label={label}
      value={normalizedValue}
      onChange={(event) => {
        updateFieldValue(setFormData, clearFieldError, name, event.target.value);
      }}
      InputLabelProps={{ shrink: true }}
      error={Boolean(errorMessage)}
      helperText={errorMessage || formData?.[name] || " "}
    />
  );
}
